import { useState, useEffect, useRef } from 'react';
import BeeloveLogo from '../components/BeeloveLogo';
import { useApp } from '../contexts/AppContext';
import { SUPPORTED_LANGS } from '../utils/i18n'
import { useTr } from '../utils/useTr';

// ── Design tokens (mirror global.css) ────────────────────────
const G = {
  gold:     '#C9A84C',
  goldLt:   '#E2C06E',
  goldPale: 'rgba(201,168,76,0.10)',
  goldBdr:  'rgba(201,168,76,0.28)',
  black:    '#0A0A0A',
  charcoal: '#111111',
  dark:     '#1C1C1C',
  white:    '#FAFAF8',
  muted:    '#888880',
  muted2:   '#444440',
  border:   'rgba(255,255,255,0.07)',
  green:    '#4A9E6B',
};

const serif = "'Cormorant Garamond', serif";
const sans  = "'Outfit', sans-serif";

// ── Scroll reveal hook ────────────────────────────────────────
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(el); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({ children, delay = 0, style = {} }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} style={{
      opacity:   visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(28px)',
      transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
      ...style,
    }}>
      {children}
    </div>
  );
}

// ── Grain overlay ─────────────────────────────────────────────
const GRAIN_SVG = "data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E";

// ── Profile data for grid / hero cards ───────────────────────
const PROFILES = [
  { name:'Camille', age:26, job:'Frontend Dev',  city:'Lyon',     score:9.2, compat:87, photo:'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=500&q=80&auto=format&fit=crop', compatColor:'#C9A84C' },
  { name:'Lucas',   age:28, job:'UX Designer',   city:'Paris',    score:8.4, compat:74, photo:'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=500&q=80&auto=format&fit=crop', compatColor:'#9CA880' },
  { name:'Léa',     age:29, job:'Musician',      city:'Nantes',   score:9.5, compat:92, photo:'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=500&q=80&auto=format&fit=crop', compatColor:'#C9A84C' },
  { name:'Théo',    age:31, job:'Chef',          city:'Bordeaux', score:8.9, compat:61, photo:'https://images.unsplash.com/photo-1552058544-f2b08422138a?w=500&q=80&auto=format&fit=crop', compatColor:'#7AB8C4' },
  { name:'Fatou',   age:26, job:'Lawyer',        city:'Montréal', score:9.1, compat:89, photo:'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=500&q=80&auto=format&fit=crop', compatColor:'#C9A84C' },
  { name:'Nathan',  age:30, job:'Architect',     city:'Montréal', score:8.7, compat:76, photo:'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=500&q=80&auto=format&fit=crop', compatColor:'#7AB8C4' },
];

function getTestimonials(tr) { return [
  { quote: tr('testimonial.1_quote'), name:'Sophie M.', role:tr('testimonial.1_role'), score:'9.4', photo:'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&q=80&auto=format&fit=crop' },
  { quote: tr('testimonial.2_quote'), name:'Antoine B.', role:tr('testimonial.2_role'), score:'8.8', photo:'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&q=80&auto=format&fit=crop' },
  { quote: tr('testimonial.3_quote'), name:'Léa R.', role:tr('testimonial.3_role'), score:'9.6', photo:'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=80&q=80&auto=format&fit=crop' },
  { quote: tr('testimonial.4_quote'), name:'Marc G.', role:tr('testimonial.4_role'), photo:'https://images.unsplash.com/photo-1463453091185-61582044d556?w=80&q=80&auto=format&fit=crop' },
]; }

function getFAQs(tr) { return [
  { q: tr('landing.faq_q1'), a: tr('landing.faq_a1') },
  { q: tr('landing.faq_q2'), a: tr('landing.faq_a2') },
  { q: tr('landing.faq_q3'), a: tr('landing.faq_a3') },
  { q: tr('landing.faq_q4'), a: tr('landing.faq_a4') },
  { q: tr('landing.faq_q5'), a: tr('landing.faq_a5') },
  { q: tr('landing.faq_q6'), a: tr('landing.faq_a6') },
]; }

const BEE_WORDS = ['Bee Loved','Bee Patient','Bee Authentic','Bee Seen','Bee Brave','Bee Open','Bee In Contact','Bee Remembered','Bee Yourself','Bee Connected','Bee Honest','Bee The One','Bee Vulnerable','Bee Present'];

// ── Main Landing component ────────────────────────────────────
// ── Cookie consent banner ─────────────────────────────────────
const CONSENT_KEY = 'beelove_cookie_consent';
function CookieBanner({ lang }) {
  const [visible, setVisible] = useState(() => !localStorage.getItem(CONSENT_KEY));
  if (!visible) return null;
  const accept = () => { localStorage.setItem(CONSENT_KEY, 'accepted'); setVisible(false); };
  const decline = () => { localStorage.setItem(CONSENT_KEY, 'declined'); setVisible(false); };
  const isFr = lang === 'fr';
  return (
    <div style={{ position:'fixed', bottom:0, left:0, right:0, zIndex:9000, background:'rgba(14,12,8,0.97)', borderTop:'1px solid rgba(201,168,76,0.25)', backdropFilter:'blur(12px)', padding:'0.9rem 1.2rem', display:'flex', flexWrap:'wrap', alignItems:'center', gap:'0.8rem', maxWidth:'100%' }}>
      <div style={{ flex:1, minWidth:200 }}>
        <p style={{ margin:0, fontSize:'0.75rem', color:'rgba(250,250,248,0.8)', lineHeight:1.6 }}>
          {isFr
            ? "BeeLove utilise des cookies essentiels pour l'authentification et des cookies analytiques pour améliorer l'expérience. Aucune donnée n'est vendue à des tiers."
            : "BeeLove uses essential cookies for authentication and analytics cookies to improve your experience. No data is sold to third parties."}
          {' '}<a href="#privacy"
            style={{ color:'rgba(201,168,76,0.9)', textDecoration:'underline', fontSize:'0.72rem' }}>
            {isFr ? 'Politique de confidentialité' : 'Privacy policy'} →
          </a>
        </p>
      </div>
      <div style={{ display:'flex', gap:'0.5rem', flexShrink:0 }}>
        <button onClick={decline}
          style={{ padding:'0.4rem 0.9rem', background:'transparent', border:'1px solid rgba(255,255,255,0.15)', color:'rgba(250,250,248,0.55)', fontSize:'0.72rem', cursor:'pointer', fontFamily:'Outfit', borderRadius:4 }}>
          {isFr ? 'Refuser' : 'Decline'}
        </button>
        <button onClick={accept}
          style={{ padding:'0.4rem 1.1rem', background:'#C9A84C', border:'none', color:'#0A0A0A', fontSize:'0.75rem', fontWeight:700, cursor:'pointer', fontFamily:'Outfit', borderRadius:4, letterSpacing:'0.04em' }}>
          {isFr ? 'Accepter' : 'Accept'}
        </button>
      </div>
    </div>
  );
}

export default function Landing({ onGetStarted }) {
  const tr = useTr();
  const FAQS = getFAQs(tr);
  const { lang, setLang, theme, toggleTheme, features } = useApp();
  const [scrolled,    setScrolled]    = useState(false);
  const [openFaq,     setOpenFaq]     = useState(null);
  const [drawerOpen,  setDrawerOpen]  = useState(false);
  const [realStats,   setRealStats]   = useState(null); // loaded from /stats endpoint

  useEffect(() => {
    const base = process.env.REACT_APP_SUPABASE_URL
      ? `${process.env.REACT_APP_SUPABASE_URL}/functions/v1`
      : 'https://api.beelove.me';
    fetch(`${base}/stats`)
      .then(r => r.json())
      .then(data => { if (!data.error) setRealStats(data); })
      .catch(() => {}); // silently fail — no stats shown if API unavailable
  }, []);
  const [hoveredProfile,  setHoveredProfile]  = useState(null);
  const [waitlistMode,    setWaitlistMode]    = useState(false);
  const [waitlistSent,    setWaitlistSent]    = useState(false);
  const [waitlistEmail,   setWaitlistEmail]   = useState('');
  const [waitlistName,    setWaitlistName]    = useState('');
  const [cityReqMode,     setCityReqMode]     = useState(false);
  const [cityReqSent,     setCityReqSent]     = useState(false);
  const [cityReqName,     setCityReqName]     = useState('');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setDrawerOpen(false);
  };

  const submitWaitlist = async () => {
    if (!waitlistEmail.trim()) return;
    try {
      const { supabase } = await import('../utils/supabase');
      await supabase?.rpc('join_waitlist', {
        p_email:   waitlistEmail.trim().toLowerCase(),
        p_name:    waitlistName.trim() || null,
      });
    } catch {}
    setWaitlistSent(true);
  };

  const submitCityRequest = async () => {
    if (!cityReqName.trim()) return;
    try {
      const base = process.env.REACT_APP_SUPABASE_URL
        ? `${process.env.REACT_APP_SUPABASE_URL}/functions/v1` : '';
      if (base) {
        const pos = await new Promise(res =>
          navigator.geolocation?.getCurrentPosition(res, () => res(null), {timeout:3000})
        );
        await fetch(`${base}/cities/request`, {
          method:'POST', headers:{'Content-Type':'application/json'},
          body: JSON.stringify({
            cityName: cityReqName.trim(),
            lat: pos?.coords?.latitude  || null,
            lng: pos?.coords?.longitude || null,
          }),
        });
      }
    } catch {}
    setCityReqSent(true);
  };

  return (
    <div style={{ background: G.black, color: G.white, fontFamily: sans, overflowX: 'hidden' }}>

      {/* ── Grain overlay ── */}
      <div style={{ position:'fixed', inset:'-50%', width:'200%', height:'200%', opacity:.025, pointerEvents:'none', zIndex:1, backgroundImage:`url("${GRAIN_SVG}")`, backgroundSize:'150px', animation:'grain .5s steps(1) infinite' }}/>
      <style>{`
        @keyframes grain{0%,100%{transform:translate(0,0)}10%{transform:translate(-2%,-3%)}20%{transform:translate(2%,3%)}30%{transform:translate(-3%,2%)}40%{transform:translate(3%,-1%)}50%{transform:translate(-1%,3%)}60%{transform:translate(2%,-3%)}70%{transform:translate(-3%,1%)}80%{transform:translate(3%,3%)}90%{transform:translate(-2%,-1%)}}
        @keyframes marquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}
        @keyframes floatA{0%,100%{transform:rotate(-3deg) translateY(0)}50%{transform:rotate(-3deg) translateY(-10px)}}
        @keyframes floatB{0%,100%{transform:rotate(2deg) translateY(0)}50%{transform:rotate(2deg) translateY(-8px)}}
        @keyframes floatC{0%,100%{transform:rotate(-1deg) translateY(0)}50%{transform:rotate(-1deg) translateY(-12px)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
        @keyframes drawerIn{from{transform:translateX(100%)}to{transform:translateX(0)}}
        .lp-tile:hover .lp-tile-overlay{opacity:1!important}
        .lp-tile:hover .lp-tile-info{opacity:1!important;transform:translateY(0)!important}
        .lp-tile:hover img{transform:scale(1.06)!important}
        .lp-step:hover{background:rgba(201,168,76,0.04)!important}
        .lp-step:hover .step-num{color:rgba(201,168,76,0.28)!important}
        .lp-feature:hover{background:rgba(255,255,255,0.02)!important}
        .lp-tcard:hover{border-color:rgba(201,168,76,0.28)!important}
        .lp-tcard:hover::before{opacity:1!important}
        .lp-btn-ghost:hover{border-color:rgba(201,168,76,0.28)!important;color:${G.white}!important}
        .lp-plan-free:hover{background:rgba(255,255,255,0.02)!important}
        .lp-plan-royal:hover{background:rgba(255,255,255,0.02)!important}
        .lp-faq-btn:hover{color:${G.gold}!important}
        .lp-footer-link:hover{color:${G.white}!important}
        .lp-nav-link:hover{color:${G.white}!important}
      `}</style>

      {/* ── NAV ── */}
      <nav style={{ position:'fixed', top:0, left:0, right:0, zIndex:100, padding:'1.1rem clamp(1.25rem,6vw,3rem)', display:'flex', alignItems:'center', justifyContent:'space-between', background: scrolled ? 'rgba(10,10,10,0.9)' : 'transparent', backdropFilter: scrolled ? 'blur(20px)' : 'none', borderBottom: scrolled ? `1px solid ${G.border}` : 'none', transition:'all 0.4s' }}>
        <div style={{ cursor:'pointer' }} onClick={() => window.scrollTo({ top:0, behavior:'smooth' })}>
          <BeeloveLogo variant="compact" height={32} animate={false} themeOverride="dark" />
        </div>
        {/* Desktop links */}
        <div style={{ display:'flex', alignItems:'center', gap:'2rem' }} className="lp-desktop-nav">
          {[['how',tr('landing.nav_how')||'How it works'],['profiles',tr('landing.nav_profiles')||'Profiles'],...(features?.landing_pricing!==false?[['pricing',tr('landing.nav_pricing')||'Pricing']]:[]),...(features?.landing_faq!==false?[['faq',tr('landing.nav_faq')||'FAQ']]:[])].map(([id,label]) => (
            <button key={id} onClick={() => scrollTo(id)} className="lp-nav-link" style={{ background:'none', border:'none', fontSize:'.78rem', letterSpacing:'.12em', textTransform:'uppercase', color:G.muted, cursor:'pointer', fontFamily:sans, transition:'color .2s' }}>{label}</button>
          ))}
          {/* Language selector */}
          <div style={{ display:'flex', border:`1px solid ${G.goldBdr}`, borderRadius:99, overflow:'hidden' }}>
            {SUPPORTED_LANGS.map((l, i) => (
              <button key={l.code} onClick={() => setLang(l.code)}
                style={{ background: lang===l.code ? G.goldPale : 'none', border:'none', borderLeft: i>0 ? `1px solid ${G.goldBdr}` : 'none', cursor:'pointer', padding:'.26rem .55rem', fontFamily:sans, fontSize:'.72rem', fontWeight:700, color: lang===l.code ? G.gold : G.muted, letterSpacing:'.04em', transition:'all .15s' }}>
                {l.code.toUpperCase()}
              </button>
            ))}
          </div>
          <button onClick={onGetStarted} style={{ padding:'.5rem 1.2rem', border:`1px solid ${G.goldBdr}`, background:'transparent', color:G.gold, fontFamily:sans, fontSize:'.78rem', fontWeight:500, letterSpacing:'.1em', textTransform:'uppercase', cursor:'pointer', transition:'all .2s' }}
            onMouseEnter={e => e.currentTarget.style.background = G.goldPale}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >{tr('landing.hero_signin')}</button>
        </div>
        {/* Hamburger */}
        <button onClick={() => setDrawerOpen(true)} style={{ display:'none', background:'none', border:'none', color:G.white, cursor:'pointer', padding:'.3rem' }} className="lp-hamburger">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
        </button>
        <style>{`@media(max-width:768px){.lp-desktop-nav{display:none!important}.lp-hamburger{display:flex!important;align-items:center}}`}</style>
      </nav>

      {/* ── MOBILE DRAWER ── */}
      {drawerOpen && (
        <div style={{ position:'fixed', inset:0, zIndex:200 }}>
          <div onClick={() => setDrawerOpen(false)} style={{ position:'absolute', inset:0, background:'var(--overlay-modal)', backdropFilter:'blur(4px)' }}/>
          <div style={{ position:'absolute', top:0, right:0, bottom:0, width:'min(320px,85vw)', background:G.charcoal, borderLeft:`1px solid ${G.border}`, padding:'2rem 1.5rem', display:'flex', flexDirection:'column', gap:'1.5rem', animation:'drawerIn .3s ease' }}>
            <button onClick={() => setDrawerOpen(false)} style={{ alignSelf:'flex-end', background:'none', border:'none', color:G.muted, fontSize:'1.3rem', cursor:'pointer' }}>✕</button>
            {[['how',tr('landing.nav_how')],['profiles',tr('landing.profiles_nav')],...(features?.landing_pricing!==false?[['pricing',tr('landing.nav_pricing')]]:[]),...(features?.landing_faq!==false?[['faq',tr('landing.nav_faq')]]:[])].map(([id,label]) => (
              <button key={id} onClick={() => scrollTo(id)} style={{ background:'none', border:'none', borderBottom:`1px solid ${G.border}`, color:G.white, fontFamily:serif, fontSize:'1.6rem', fontStyle:'italic', textAlign:'left', padding:'1rem 0', cursor:'pointer' }}>{label}</button>
            ))}
            <button onClick={() => { setDrawerOpen(false); onGetStarted(); }} style={{ padding:'.9rem', background:`linear-gradient(135deg,${G.gold},#B8922A)`, border:'none', color:G.black, fontFamily:sans, fontWeight:600, fontSize:'.88rem', letterSpacing:'.08em', textTransform:'uppercase', cursor:'pointer' }}>
              {tr('landing.hero_cta')} — <em style={{ fontFamily:serif, fontStyle:'italic', fontWeight:300 }}>Bee Loved</em> ✦
            </button>
            {/* Language switcher in drawer */}
            <div style={{ display:'flex', gap:'.4rem', justifyContent:'center' }}>
              {SUPPORTED_LANGS.map(l => (
                <button key={l.code} onClick={() => { setLang(l.code); setDrawerOpen(false); }}
                  style={{ padding:'.35rem .85rem', background: lang===l.code ? G.goldPale : 'transparent', border:`1px solid ${lang===l.code ? G.gold : G.border}`, borderRadius:99, color: lang===l.code ? G.gold : G.muted, fontFamily:sans, fontSize:'.75rem', fontWeight:700, cursor:'pointer' }}>
                  {l.code.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          HERO
      ══════════════════════════════════════════════ */}
      <section style={{ position:'relative', minHeight:'100svh', display:'grid', alignItems:'end', overflow:'hidden' }}>
        {/* bg */}
        <div style={{ position:'absolute', inset:0 }}>
          <img src="https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=1600&q=85&auto=format&fit=crop" alt="" style={{ width:'100%', height:'100%', objectFit:'cover', objectPosition:'center 20%', filter:'brightness(.42)' }}/>
          <div style={{ position:'absolute', inset:0, background:'linear-gradient(160deg,rgba(10,10,10,.3) 0%,rgba(10,10,10,.7) 60%,#0A0A0A 100%)' }}/>
        </div>

        <div style={{ position:'relative', zIndex:2, padding:'clamp(4rem,12vw,9rem) clamp(1.25rem,6vw,3rem)', maxWidth:1300, margin:'0 auto', width:'100%', display:'grid', gridTemplateColumns:'1fr', gap:'3rem', alignItems:'end' }}>
          <style>{`@media(min-width:1024px){.hero-grid{grid-template-columns:1fr 1fr!important}}`}</style>
          <div className="hero-grid" style={{ display:'grid', gridTemplateColumns:'1fr', gap:'3rem', alignItems:'end' }}>
            <div>
              {/* Hero logo — animated EKG draws on page load */}
              <div style={{ marginBottom:'1.8rem', animation:'fadeUp .5s ease both' }}>
                <BeeloveLogo variant="full" height={56} animate={true} themeOverride="dark" />
              </div>

              {/* Eyebrow */}
              <div style={{ display:'flex', alignItems:'center', gap:'.6rem', marginBottom:'1.5rem', animation:'fadeUp .6s ease both' }}>
                <div style={{ width:24, height:1, background:G.gold }}/>
                <span style={{ fontSize:'.65rem', letterSpacing:'.18em', textTransform:'uppercase', color:G.gold }}>{tr('landing.tagline')}</span>
              </div>

              <h1 style={{ fontFamily:serif, fontSize:'clamp(3.2rem,8vw,6.5rem)', fontWeight:300, lineHeight:1.0, marginBottom:'1.5rem', letterSpacing:'-.01em', animation:'fadeUp .6s ease .1s both' }}>
                {tr('landing.hero_dont_just')}<br/>
                <em style={{ fontStyle:'italic', color:G.gold }}>Bee</em> here —<br/>
                <strong style={{ fontWeight:700 }}>Bee Loved.</strong>
              </h1>

              <p style={{ fontSize:'clamp(.9rem,1.5vw,1.05rem)', color:'rgba(250,250,248,.65)', lineHeight:1.75, maxWidth:480, marginBottom:'2.5rem', animation:'fadeUp .6s ease .2s both' }}>
                {tr('landing.hero_sub')}
              </p>

              <div style={{ display:'flex', flexWrap:'wrap', gap:'.7rem', marginBottom:'2.5rem', animation:'fadeUp .6s ease .3s both' }}>
                {features?.landing_waitlist !== false
                  ? <button onClick={() => setWaitlistMode(true)} style={{ padding:'.9rem 2rem', background:`linear-gradient(135deg,${G.gold},#B8922A)`, border:'none', color:G.black, fontFamily:sans, fontSize:'.88rem', fontWeight:600, letterSpacing:'.08em', textTransform:'uppercase', cursor:'pointer' }}>
                      {tr('landing.hero_cta')} — <em style={{ fontFamily:serif, fontStyle:'italic', fontWeight:300 }}>Bee Loved</em> ✦
                    </button>
                  : <button onClick={onGetStarted} style={{ padding:'.9rem 2rem', background:`linear-gradient(135deg,${G.gold},#B8922A)`, border:'none', color:G.black, fontFamily:sans, fontSize:'.88rem', fontWeight:600, letterSpacing:'.08em', textTransform:'uppercase', cursor:'pointer' }}>
                      {tr('landing.hero_signin')} — <em style={{ fontFamily:serif, fontStyle:'italic', fontWeight:300 }}>Bee Loved</em> ✦
                    </button>
                }
                <button onClick={() => scrollTo('how')} className="lp-btn-ghost" style={{ padding:'.9rem 1.8rem', border:`1px solid rgba(250,250,248,.2)`, background:'transparent', color:'rgba(250,250,248,.8)', fontFamily:sans, fontSize:'.88rem', cursor:'pointer', transition:'all .25s' }}>
                  {tr('landing.how_arrow')}
                </button>
              </div>

              {/* Stats — gated by landing_stats */}
              {features?.landing_stats !== false && <div style={{ display:'flex', gap:'2rem', paddingTop:'2rem', borderTop:`1px solid ${G.border}`, animation:'fadeUp .6s ease .4s both' }}>
                {(() => {
                  // Only show stats that meet thresholds (set by admin)
                  const s = realStats;
                  const rows = [
                    s?.users    ? [s.users.toLocaleString(lang==='fr'?'fr-CA':'en-CA'), tr('landing.stat_members')] : null,
                    s?.quizPct ? [`${s.quizPct}%`, tr('landing.stat_quiz')] : null,
                    s?.matches  ? [s.matches.toLocaleString(lang==='fr'?'fr-CA':'en-CA'), tr('landing.stat_matches')] : null,
                    s?.networks ? [s.networks, tr('landing.stat_networks')] : null,
                  ].filter(Boolean);
                  if (rows.length === 0) return null; // no stats yet — show nothing
                  return rows.map(([n, l]) => (
                    <div key={l}>
                      <div style={{ fontFamily:serif, fontSize:'1.8rem', fontWeight:600, color:G.gold, lineHeight:1 }}>{n}</div>
                      <div style={{ fontSize:'.62rem', letterSpacing:'.15em', textTransform:'uppercase', color:G.muted, marginTop:'.2rem' }}>{l}</div>
                    </div>
                  ));
                })()}
              </div>}
            </div>

            {/* Floating cards — desktop only — gated by landing_photos */}
            {features?.landing_photos !== false && <div style={{ position:'relative', height:520, display:'none' }} className="lp-hero-cards">
              <style>{`@media(min-width:1024px){.lp-hero-cards{display:block!important}}`}</style>
              {[
                { name:'Léa, 29', meta:'Musician · Nantes', pct:95, photo:'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=400&q=80&auto=format&fit=crop', top:40, left:20, anim:'floatA 6s ease-in-out infinite', rot:'-3deg', w:200, z:1 },
                { name:'Lucas, 28', meta:'Designer · Paris', pct:84, photo:'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80&auto=format&fit=crop', top:60, left:160, anim:'floatB 7s ease-in-out infinite .5s', rot:'2deg', w:200, z:2 },
                { name:'Fatou, 26', meta:'Lawyer · Montréal', pct:91, photo:'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&q=80&auto=format&fit=crop', top:230, left:60, anim:'floatC 5.5s ease-in-out infinite 1s', rot:'-1deg', w:220, z:3 },
              ].map((c, i) => (
                <div key={i} style={{ position:'absolute', width:c.w, top:c.top, left:c.left, zIndex:c.z, background:G.charcoal, border:`1px solid ${G.border}`, boxShadow:'0 24px 64px rgba(0,0,0,.6)', animation:c.anim, transform:`rotate(${c.rot})` }}>
                  <div style={{ position:'absolute', top:0, left:0, right:0, height:1, background:`linear-gradient(90deg,transparent,${G.gold},transparent)` }}/>
                  <img src={c.photo} alt={c.name} style={{ width:'100%', height:240, objectFit:'cover', display:'block' }}/>
                  <div style={{ padding:'.8rem' }}>
                    <div style={{ fontFamily:serif, fontSize:'1rem', fontWeight:500, marginBottom:'.15rem' }}>{c.name}</div>
                    <div style={{ fontSize:'.7rem', color:G.muted, marginBottom:'.5rem' }}>{c.meta}</div>
                    <div style={{ display:'flex', alignItems:'center', gap:'.4rem' }}>

                      <div style={{ flex:1, height:1, background:G.border }}>
                        <div style={{ height:'100%', width:`${c.pct}%`, background:`linear-gradient(90deg,${G.gold},${G.goldLt})` }}/>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>}
          </div>
        </div>
      </section>

      {/* ── MARQUEE ── */}
      <div style={{ overflow:'hidden', padding:'.9rem 0', borderTop:`1px solid ${G.border}`, borderBottom:`1px solid ${G.border}`, background:G.charcoal }}>
        <div style={{ display:'flex', width:'max-content', animation:'marquee 28s linear infinite' }}>
          {[...BEE_WORDS, ...BEE_WORDS].map((w, i) => (
            <span key={i} style={{ padding:'0 2.5rem', fontFamily:serif, fontSize:'1rem', fontStyle:'italic', color:G.gold, whiteSpace:'nowrap', borderRight:`1px solid ${G.goldBdr}` }}>{w}</span>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════════
          HOW IT WORKS
      ══════════════════════════════════════════════ */}
      <div id="how" style={{ background:G.charcoal, borderTop:`1px solid ${G.border}`, borderBottom:`1px solid ${G.border}` }}>
        <div style={{ textAlign:'center', padding:'clamp(4.5rem,12vw,9rem) clamp(1.25rem,6vw,3rem) clamp(2rem,6vw,4rem)' }}>
          <Reveal>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'.8rem', marginBottom:'1.2rem' }}>
              <span style={{ fontSize:'.55rem', color:G.gold }}>✦</span>
              <span style={{ fontSize:'.62rem', letterSpacing:'.2em', textTransform:'uppercase', color:G.gold }}>{tr('landing.concept')}</span>
            </div>
            <h2 style={{ fontFamily:serif, fontSize:'clamp(2.2rem,5vw,3.8rem)', fontWeight:300, lineHeight:1.1, marginBottom:'1rem' }}>
              {tr('landing.four_steps_to')} <em style={{ color:G.gold, fontStyle:'italic' }}>Bee Loved</em>
            </h2>
            <p style={{ color:G.muted, fontSize:'clamp(.88rem,1.4vw,1rem)', lineHeight:1.8, maxWidth:560, margin:'0 auto' }}>
              {tr('landing.no_swiping')} {tr('landing.trust_sub')}
            </p>
          </Reveal>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', borderTop:`1px solid ${G.border}` }}>
          {[
            { n:'01', title:tr('landing.step1_title'), desc:tr('landing.how_step1_body'), tag:tr('common.free') },
            { n:'02', title:tr('landing.step2_title'),  desc:tr('landing.how_step2_body'), tag:tr('landing.step_tag_friends') },
            { n:'03', title:tr('landing.step3_title'),  desc:tr('landing.how_step3_body'), tag:tr('landing.step_tag_smart') },
            { n:'04', title:tr('landing.step4_title'),   desc:tr('landing.how_step4_body'), tag:tr('landing.step_tag_real') },
          ].map((s, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="lp-step" style={{ padding:'clamp(2rem,5vw,3.5rem) clamp(1.5rem,4vw,2.5rem)', borderRight:`1px solid ${G.border}`, transition:'background .3s', cursor:'default' }}>
                <div className="step-num" style={{ fontFamily:serif, fontSize:'4rem', fontWeight:700, lineHeight:1, color:'rgba(201,168,76,0.12)', marginBottom:'1.2rem', transition:'color .3s' }}>{s.n}</div>
                <div style={{ fontFamily:serif, fontSize:'1.35rem', fontWeight:500, marginBottom:'.6rem' }}>
                  <em style={{ color:G.gold, fontStyle:'italic' }}>{s.title.split(' ')[0]}</em>{' '}{s.title.split(' ').slice(1).join(' ')}
                </div>
                <p style={{ fontSize:'.83rem', color:G.muted, lineHeight:1.7, marginBottom:'1rem' }}>{s.desc}</p>
                <span style={{ display:'inline-block', fontSize:'.6rem', letterSpacing:'.15em', textTransform:'uppercase', color:G.gold, border:`1px solid ${G.goldBdr}`, padding:'.25rem .7rem' }}>{s.tag}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════════
          PROFILES GRID — gated by landing_photos
      ══════════════════════════════════════════════ */}
      {features?.landing_photos !== false && <div id="profiles">
        <div style={{ padding:'clamp(4.5rem,12vw,9rem) clamp(1.25rem,6vw,3rem) 0', maxWidth:1300, margin:'0 auto' }}>
          <Reveal>
            <div style={{ display:'flex', alignItems:'center', gap:'.8rem', marginBottom:'1.2rem' }}>
              <span style={{ fontSize:'.55rem', color:G.gold }}>✦</span>
              <span style={{ fontSize:'.62rem', letterSpacing:'.2em', textTransform:'uppercase', color:G.gold }}>{tr('landing.real_members')}</span>
            </div>
            <h2 style={{ fontFamily:serif, fontSize:'clamp(2.2rem,5vw,3.8rem)', fontWeight:300, lineHeight:1.1, marginBottom:'1rem' }}>
              {tr('landing.people_worth_knowing')}
            </h2>
            <p style={{ color:G.muted, fontSize:'clamp(.88rem,1.4vw,1rem)', lineHeight:1.8 }}>
              {tr('landing.trust_sub')}
            </p>
          </Reveal>
        </div>

        <Reveal style={{ maxWidth:1300, margin:'clamp(2.5rem,6vw,4rem) auto 0' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:1, background:G.border }}>
            <style>{`@media(min-width:640px){.profiles-grid{grid-template-columns:repeat(2,1fr)!important}}@media(min-width:640px){.profiles-grid{grid-template-columns:repeat(3,1fr)!important}}@media(min-width:1024px){.profiles-grid{grid-template-columns:repeat(6,1fr)!important}}`}</style>
            <div className="profiles-grid" style={{ display:'contents' }}>
              {PROFILES.map((p, i) => (
                <div key={i} className="lp-tile" style={{ position:'relative', aspectRatio:'3/4', overflow:'hidden', cursor:'pointer' }}
                  onMouseEnter={() => setHoveredProfile(i)} onMouseLeave={() => setHoveredProfile(null)}
                >
                  <img src={p.photo} alt={p.name} style={{ width:'100%', height:'100%', objectFit:'cover', transition:'transform .6s ease, filter .4s' }}/>


                  <div className="lp-tile-overlay" style={{ position:'absolute', inset:0, background:'linear-gradient(transparent 45%,rgba(0,0,0,.85) 100%)', opacity:0, transition:'opacity .3s' }}/>
                  <div className="lp-tile-info" style={{ position:'absolute', bottom:0, left:0, right:0, padding:'1rem', transform:'translateY(8px)', opacity:0, transition:'all .3s' }}>
                    <div style={{ fontFamily:serif, fontSize:'1rem', fontWeight:500, marginBottom:'.15rem' }}>{p.name}, {p.age}</div>
                    <div style={{ fontSize:'.68rem', color:'rgba(250,250,248,.65)' }}>{p.job} · {p.city}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <div style={{ textAlign:'center', padding:'clamp(2rem,5vw,3.5rem) clamp(1.25rem,6vw,3rem)', borderTop:`1px solid ${G.border}`, maxWidth:1300, margin:'0 auto' }}>
          <p style={{ color:G.muted, fontSize:'.88rem', marginBottom:'1.2rem' }}>{tr('landing.hundreds')}</p>
          <button onClick={onGetStarted} style={{ padding:'.85rem 2rem', background:`linear-gradient(135deg,${G.gold},#B8922A)`, border:'none', color:G.black, fontFamily:sans, fontWeight:600, fontSize:'.85rem', letterSpacing:'.08em', textTransform:'uppercase', cursor:'pointer' }}>
            {tr('landing.see_waiting')}
          </button>
        </div>
      </div>}

      {/* ══════════════════════════════════════════════
          WHY BEELOVE
      ══════════════════════════════════════════════ */}
      <div style={{ background:G.charcoal, borderTop:`1px solid ${G.border}`, borderBottom:`1px solid ${G.border}` }}>
        <div style={{ padding:'clamp(4.5rem,12vw,9rem) clamp(1.25rem,6vw,3rem)', maxWidth:1300, margin:'0 auto', display:'grid', gridTemplateColumns:'1fr', gap:'clamp(3rem,7vw,5rem)', alignItems:'center' }}>
          <style>{`@media(min-width:1024px){.why-grid{grid-template-columns:1fr 1fr!important}}`}</style>
          <div className="why-grid" style={{ display:'grid', gridTemplateColumns:'1fr', gap:'clamp(3rem,7vw,5rem)', alignItems:'center' }}>
            <div>
              <Reveal>
                <div style={{ display:'flex', alignItems:'center', gap:'.8rem', marginBottom:'1.2rem' }}>
                  <span style={{ fontSize:'.55rem', color:G.gold }}>✦</span>
                  <span style={{ fontSize:'.62rem', letterSpacing:'.2em', textTransform:'uppercase', color:G.gold }}>{tr('landing.why_title')}</span>
                </div>
                <h2 style={{ fontFamily:serif, fontSize:'clamp(2.2rem,5vw,3.8rem)', fontWeight:300, lineHeight:1.1, marginBottom:'1rem' }}>
                  {tr('landing.broken_1')} <em style={{ color:G.gold, fontStyle:'italic' }}>{tr('landing.broken_2')}</em><br/>{tr('landing.we_fixed')}
                </h2>
                <p style={{ color:G.muted, fontSize:'clamp(.88rem,1.4vw,1rem)', lineHeight:1.8, marginBottom:'2rem' }}>
                  {tr('landing.every_other_app')} {tr('landing.give_best_shot')}
                </p>
              </Reveal>
              {[
                { icon:'★', title:tr('landing.feat1_title'), desc:tr('landing.feat1_desc') },
                { icon:'✦', title:tr('landing.feat2_title'), desc:tr('landing.feat2_desc') },
                { icon:'✦', title:tr('landing.feat3_title'), desc:tr('landing.feat3_desc') },
                { icon:'✦', title:tr('landing.feat4_title'), desc:tr('landing.feat4_desc') },
              ].map((f, i) => (
                <Reveal key={i} delay={i * 0.1}>
                  <div className="lp-feature" style={{ display:'flex', gap:'1.2rem', padding:'1.4rem 0', borderBottom:`1px solid ${G.border}`, transition:'background .2s' }}>
                    <div style={{ width:40, height:40, background:G.goldPale, border:`1px solid ${G.goldBdr}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:'1rem' }}>{f.icon}</div>
                    <div>
                      <h4 style={{ fontFamily:serif, fontSize:'1.05rem', fontWeight:500, marginBottom:'.25rem' }}>{f.title}</h4>
                      <p style={{ fontSize:'.8rem', color:G.muted, lineHeight:1.65 }}>{f.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            {/* Phone mockup */}
            <Reveal delay={0.2}>
              <div style={{ maxWidth:300, margin:'0 auto', position:'relative' }}>
                <div style={{ background:G.dark, border:`1px solid ${G.border}`, borderRadius:32, overflow:'hidden', padding:12, boxShadow:'0 40px 100px rgba(0,0,0,.6)' }}>
                  <div style={{ background:G.charcoal, borderRadius:22, overflow:'hidden', aspectRatio:'9/19' }}>
                    <img src="https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=400&q=80&auto=format&fit=crop" alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                  </div>
                </div>
                <div style={{ position:'absolute', bottom:-40, left:'50%', transform:'translateX(-50%)', width:200, height:80, background:'radial-gradient(ellipse,rgba(201,168,76,.2) 0%,transparent 70%)', pointerEvents:'none' }}/>
              </div>
            </Reveal>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════
          TESTIMONIALS
      ══════════════════════════════════════════════ */}
      <div>
        <div style={{ padding:'clamp(4.5rem,12vw,9rem) clamp(1.25rem,6vw,3rem) clamp(2rem,5vw,3rem)', maxWidth:1300, margin:'0 auto' }}>
          <Reveal>
            <div style={{ display:'flex', alignItems:'center', gap:'.8rem', marginBottom:'1.2rem' }}>
              <span style={{ fontSize:'.55rem', color:G.gold }}>✦</span>
              <span style={{ fontSize:'.62rem', letterSpacing:'.2em', textTransform:'uppercase', color:G.gold }}>{tr('landing.real_stories')}</span>
            </div>
            <h2 style={{ fontFamily:serif, fontSize:'clamp(2.2rem,5vw,3.8rem)', fontWeight:300, lineHeight:1.1 }}>
              {tr('landing.they_found')}<br/>{tr('landing.bee_together')} <em style={{ color:G.gold, fontStyle:'italic' }}>Bee</em> {tr('landing.together')}
            </h2>
          </Reveal>
        </div>
        <div style={{ display:'flex', gap:'1.5rem', padding:'0 clamp(1.25rem,6vw,3rem) clamp(4.5rem,12vw,9rem)', overflowX:'auto', scrollSnapType:'x mandatory', WebkitOverflowScrolling:'touch', scrollbarWidth:'none', maxWidth:1300, margin:'0 auto' }}>
          {getTestimonials(tr).map((t, i) => (
            <Reveal key={i} delay={i * 0.1} style={{ flex:'0 0 clamp(280px,80vw,380px)', scrollSnapAlign:'start' }}>
              <div className="lp-tcard" style={{ background:G.charcoal, border:`1px solid ${G.border}`, padding:'clamp(1.5rem,4vw,2.2rem)', position:'relative', transition:'border-color .3s', height:'100%' }}>
                <div className="lp-tcard-line" style={{ position:'absolute', top:0, left:0, right:0, height:1, background:`linear-gradient(90deg,transparent,${G.gold},transparent)`, opacity:0, transition:'opacity .3s' }}/>
                <div style={{ fontFamily:serif, fontSize:'2rem', color:G.gold, lineHeight:.5, marginBottom:'.5rem' }}>"</div>
                <p style={{ fontFamily:serif, fontSize:'clamp(1rem,2vw,1.15rem)', fontStyle:'italic', lineHeight:1.75, color:'rgba(250,250,248,.85)', marginBottom:'1.5rem' }}>{t.quote}</p>
                <div style={{ display:'flex', alignItems:'center', gap:'.8rem' }}>
                  <div style={{ width:40, height:40, borderRadius:2, overflow:'hidden', flexShrink:0, border:`1px solid ${G.border}` }}>
                    <img src={t.photo} alt={t.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                  </div>
                  <div>
                    <div style={{ fontSize:'.85rem', fontWeight:500, marginBottom:'.1rem' }}>{t.name}</div>
                    <div style={{ fontSize:'.7rem', color:G.muted }}>{t.role}</div>
                  </div>

                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════════
          PRICING — gated by landing_pricing
      ══════════════════════════════════════════════ */}
      {features?.landing_pricing !== false && <div id="pricing" style={{ background:G.charcoal, borderTop:`1px solid ${G.border}`, borderBottom:`1px solid ${G.border}` }}>
        <div style={{ textAlign:'center', padding:'clamp(4.5rem,12vw,9rem) clamp(1.25rem,6vw,3rem) clamp(2.5rem,6vw,4rem)' }}>
          <Reveal>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'.8rem', marginBottom:'1.2rem' }}>
              <span style={{ fontSize:'.55rem', color:G.gold }}>✦</span>
              <span style={{ fontSize:'.62rem', letterSpacing:'.2em', textTransform:'uppercase', color:G.gold }}>{tr('landing.nav_pricing')}</span>
            </div>
            <h2 style={{ fontFamily:serif, fontSize:'clamp(2.2rem,5vw,3.8rem)', fontWeight:300, lineHeight:1.1, marginBottom:'1rem' }}>
              {tr('landing.start_free_cta')}<br/><em style={{ color:G.gold, fontStyle:'italic' }}>Bee Royal</em> {tr('landing.bee_royal_ready')}
            </h2>
            <p style={{ color:G.muted, fontSize:'clamp(.88rem,1.4vw,1rem)', lineHeight:1.8 }}>
              {tr('landing.no_dark')}
            </p>
          </Reveal>
        </div>

        <div style={{ maxWidth:1300, margin:'0 auto', display:'grid', gridTemplateColumns:'1fr', gap:1, background:G.border, border:`1px solid ${G.border}` }}>
          <style>{`@media(min-width:768px){.pricing-grid{grid-template-columns:repeat(3,1fr)!important}}`}</style>
          <div className="pricing-grid" style={{ display:'grid', gridTemplateColumns:'1fr', gap:1 }}>
            {/* Free */}
            <Reveal>
              <div className="lp-plan-free" style={{ padding:'clamp(2rem,5vw,3rem) clamp(1.5rem,4vw,2.5rem)', background:G.charcoal, transition:'background .3s', height:'100%' }}>
                <div style={{ fontSize:'.62rem', letterSpacing:'.2em', textTransform:'uppercase', color:G.gold, marginBottom:'.8rem' }}>{tr('landing.plan_free_label')}</div>
                <div style={{ fontFamily:serif, fontSize:'1.6rem', fontWeight:500, marginBottom:'.3rem' }}><em style={{ fontStyle:'italic', color:G.gold }}>Bee</em> Here</div>
                <div style={{ fontFamily:serif, fontSize:'2rem', fontWeight:600, color:G.gold, margin:'1.2rem 0 1.8rem' }}>Free</div>
                {[tr('landing.plan_feat_likes'),tr('landing.plan_feat_quiz'),tr('landing.plan_feat_ai_bio'),tr('landing.plan_feat_invites'),tr('landing.plan_feat_hive')].map(f => (
                  <div key={f} style={{ display:'flex', alignItems:'flex-start', gap:'.7rem', fontSize:'.82rem', color:'rgba(250,250,248,.75)', marginBottom:'.7rem' }}>
                    <span style={{ color:G.gold, fontSize:'.55rem', marginTop:'.35rem', flexShrink:0 }}>✦</span>{f}
                  </div>
                ))}
                {[tr('landing.plan_feat_liked'),tr('landing.plan_feat_unlimited'),tr('landing.plan_feat_boost')].map(f => (
                  <div key={f} style={{ display:'flex', alignItems:'flex-start', gap:'.7rem', fontSize:'.82rem', color:G.muted2, marginBottom:'.7rem' }}>
                    <span style={{ color:G.muted2, fontSize:'.55rem', marginTop:'.35rem', flexShrink:0 }}>✦</span>{f}
                  </div>
                ))}
                <button onClick={onGetStarted} style={{ width:'100%', padding:'.85rem', border:`1px solid ${G.goldBdr}`, background:'transparent', color:G.gold, fontFamily:sans, fontSize:'.8rem', fontWeight:500, letterSpacing:'.1em', textTransform:'uppercase', cursor:'pointer', marginTop:'1.5rem', transition:'all .2s' }}
                  onMouseEnter={e => e.currentTarget.style.background = G.goldPale}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >{tr('landing.hero_cta')} ✦</button>
              </div>
            </Reveal>

            {/* Honey — featured */}
            <Reveal delay={0.1}>
              <div style={{ padding:'clamp(2rem,5vw,3rem) clamp(1.5rem,4vw,2.5rem)', background:G.dark, position:'relative', height:'100%' }}>
                <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:`linear-gradient(90deg,transparent,${G.gold},transparent)` }}/>
                <div style={{ position:'absolute', top:-1, right:'2rem', background:G.gold, color:G.black, fontSize:'.6rem', fontWeight:700, letterSpacing:'.12em', textTransform:'uppercase', padding:'.3rem .8rem' }}>{tr('landing.plan_honey_label')}</div>
                <div style={{ fontSize:'.62rem', letterSpacing:'.2em', textTransform:'uppercase', color:G.gold, marginBottom:'.8rem' }}>{tr('landing.plan_royal_label')}</div>
                <div style={{ fontFamily:serif, fontSize:'1.6rem', fontWeight:500, marginBottom:'.3rem' }}><em style={{ fontStyle:'italic', color:G.gold }}>Bee</em> Honey</div>
                <div style={{ display:'flex', alignItems:'baseline', gap:'.4rem', margin:'1.2rem 0 1.8rem' }}>
                  <span style={{ fontSize:'1.2rem', color:G.muted, alignSelf:'flex-start', marginTop:'.5rem' }}>€</span>
                  <span style={{ fontFamily:serif, fontSize:'3rem', fontWeight:600, lineHeight:1 }}>14</span>
                  <span style={{ fontSize:'.78rem', color:G.muted }}>{tr('landing.month_label')}</span>
                </div>
                {[tr('landing.plan_honey_likes'),tr('landing.plan_honey_liked'),tr('landing.plan_honey_quiz'),tr('landing.plan_honey_bio'),tr('landing.plan_honey_invites'),tr('landing.plan_honey_boost'),tr('landing.plan_honey_analytics')].map(f => (
                  <div key={f} style={{ display:'flex', alignItems:'flex-start', gap:'.7rem', fontSize:'.82rem', color:'rgba(250,250,248,.75)', marginBottom:'.7rem' }}>
                    <span style={{ color:G.gold, fontSize:'.55rem', marginTop:'.35rem', flexShrink:0 }}>✦</span>{f}
                  </div>
                ))}
                <button onClick={onGetStarted} style={{ width:'100%', padding:'.85rem', background:`linear-gradient(135deg,${G.gold},#B8922A)`, border:'none', color:G.black, fontFamily:sans, fontSize:'.85rem', fontWeight:600, letterSpacing:'.08em', textTransform:'uppercase', cursor:'pointer', marginTop:'1.5rem' }}>
                  Start 7-day free trial ✦
                </button>
              </div>
            </Reveal>

            {/* Royal */}
            <Reveal delay={0.2}>
              <div className="lp-plan-royal" style={{ padding:'clamp(2rem,5vw,3rem) clamp(1.5rem,4vw,2.5rem)', background:G.charcoal, transition:'background .3s', height:'100%' }}>
                <div style={{ fontSize:'.62rem', letterSpacing:'.2em', textTransform:'uppercase', color:G.gold, marginBottom:'.8rem' }}>{tr('landing.plan_gold_label')}</div>
                <div style={{ fontFamily:serif, fontSize:'1.6rem', fontWeight:500, marginBottom:'.3rem' }}><em style={{ fontStyle:'italic', color:G.gold }}>Bee</em> Royal</div>
                <div style={{ display:'flex', alignItems:'baseline', gap:'.4rem', margin:'1.2rem 0 1.8rem' }}>
                  <span style={{ fontSize:'1.2rem', color:G.muted, alignSelf:'flex-start', marginTop:'.5rem' }}>€</span>
                  <span style={{ fontFamily:serif, fontSize:'3rem', fontWeight:600, lineHeight:1 }}>29</span>
                  <span style={{ fontSize:'.78rem', color:G.muted }}>{tr('landing.month_label')}</span>
                </div>
                {[tr('landing.plan_royal_all'),tr('landing.plan_royal_ai'),tr('landing.plan_royal_priority'),tr('landing.plan_royal_boosts'),tr('landing.plan_royal_vip'),tr('landing.plan_royal_receipts'),tr('landing.plan_royal_filters'),tr('landing.plan_royal_support')].map(f => (
                  <div key={f} style={{ display:'flex', alignItems:'flex-start', gap:'.7rem', fontSize:'.82rem', color:'rgba(250,250,248,.75)', marginBottom:'.7rem' }}>
                    <span style={{ color:G.gold, fontSize:'.55rem', marginTop:'.35rem', flexShrink:0 }}>✦</span>{f}
                  </div>
                ))}
                <button onClick={onGetStarted} style={{ width:'100%', padding:'.85rem', border:`1px solid ${G.goldBdr}`, background:'transparent', color:G.gold, fontFamily:sans, fontSize:'.8rem', fontWeight:500, letterSpacing:'.1em', textTransform:'uppercase', cursor:'pointer', marginTop:'1.5rem', transition:'all .2s' }}
                  onMouseEnter={e => e.currentTarget.style.background = G.goldPale}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >{tr('landing.go_royal')}</button>
              </div>
            </Reveal>
          </div>
        </div>
      </div>}

      {/* ══════════════════════════════════════════════
          FAQ — gated by landing_faq
      ══════════════════════════════════════════════ */}
      {features?.landing_faq !== false && <div id="faq" style={{ padding:'clamp(4.5rem,12vw,9rem) clamp(1.25rem,6vw,3rem)', maxWidth:800, margin:'0 auto' }}>
        <div style={{ textAlign:'center', marginBottom:'clamp(2.5rem,6vw,4rem)' }}>
          <Reveal>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'.8rem', marginBottom:'1.2rem' }}>
              <span style={{ fontSize:'.55rem', color:G.gold }}>✦</span>
              <span style={{ fontSize:'.62rem', letterSpacing:'.2em', textTransform:'uppercase', color:G.gold }}>{tr('landing.questions_label')}</span>
            </div>
            <h2 style={{ fontFamily:serif, fontSize:'clamp(2.2rem,5vw,3.8rem)', fontWeight:300, lineHeight:1.1 }}>
              {tr('landing.everything_need')}<br/>{tr('landing.to_bee_ready')}
            </h2>
          </Reveal>
        </div>

        <div>
          {FAQS.map((f, i) => {
            const isOpen = openFaq === i;
            return (
              <Reveal key={i} delay={i * 0.05}>
                <div style={{ borderBottom:`1px solid ${G.border}` }}>
                  <button className="lp-faq-btn" onClick={() => setOpenFaq(isOpen ? null : i)} style={{ width:'100%', display:'flex', justifyContent:'space-between', alignItems:'center', padding:'1.3rem 0', background:'none', border:'none', color: isOpen ? G.gold : G.white, fontFamily:sans, fontSize:'.95rem', textAlign:'left', gap:'1rem', cursor:'pointer', transition:'color .2s' }}>
                    <span>{f.q}</span>
                    <span style={{ width:24, height:24, border:`1px solid ${isOpen ? G.goldBdr : G.border}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:'.9rem', color: isOpen ? G.gold : G.muted, transition:'all .3s', transform: isOpen ? 'rotate(45deg)' : 'none' }}>+</span>
                  </button>
                  <div style={{ maxHeight: isOpen ? 300 : 0, overflow:'hidden', transition:'max-height .35s ease, padding .35s', paddingBottom: isOpen ? '1.3rem' : 0 }}>
                    <p style={{ fontSize:'.88rem', color:G.muted, lineHeight:1.75 }}>{f.a}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>}

      {/* ══════════════════════════════════════════════
          FINAL CTA
      ══════════════════════════════════════════════ */}
      <section style={{ position:'relative', overflow:'hidden', borderTop:`1px solid ${G.border}`, textAlign:'center', padding:'clamp(4.5rem,12vw,9rem) clamp(1.25rem,6vw,3rem)' }}>
        <div style={{ position:'absolute', inset:0, background:'radial-gradient(ellipse 80% 60% at 50% 100%,rgba(201,168,76,.1) 0%,transparent 70%)', pointerEvents:'none' }}/>
        <Reveal>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'.8rem', marginBottom:'1.2rem' }}>
            <span style={{ fontSize:'.55rem', color:G.gold }}>✦</span>
            <span style={{ fontSize:'.62rem', letterSpacing:'.2em', textTransform:'uppercase', color:G.gold }}>{tr('landing.ready')}</span>
          </div>
          <h2 style={{ fontFamily:serif, fontSize:'clamp(2.5rem,6vw,5rem)', fontWeight:300, lineHeight:1.05, marginBottom:'1.2rem' }}>
            {tr('landing.stop_waiting')}<br/>{tr('landing.the_one_find')}
          </h2>
          <p style={{ color:G.muted, fontSize:'clamp(.88rem,1.5vw,1.05rem)', marginBottom:'2.5rem' }}>
            {tr('landing.join_free')}
          </p>
          <div style={{ display:'flex', flexWrap:'wrap', justifyContent:'center', gap:'.8rem', marginBottom:'1.5rem' }}>
            <button onClick={onGetStarted} style={{ padding:'1rem 2.5rem', background:`linear-gradient(135deg,${G.gold},#B8922A)`, border:'none', color:G.black, fontFamily:sans, fontWeight:600, fontSize:'.95rem', letterSpacing:'.08em', textTransform:'uppercase', cursor:'pointer' }}>
              {tr('landing.join_free_em')}<em style={{ fontFamily:serif, fontStyle:'italic', fontWeight:300 }}>Bee Loved</em> ✦
            </button>
            <button onClick={onGetStarted} className="lp-btn-ghost" style={{ padding:'1rem 2rem', border:`1px solid rgba(250,250,248,.2)`, background:'transparent', color:'rgba(250,250,248,.8)', fontFamily:sans, fontSize:'.95rem', cursor:'pointer', transition:'all .25s' }}>
              {tr('landing.hero_signin')} →
            </button>
          </div>
          <p style={{ fontSize:'.7rem', color:G.muted2, letterSpacing:'.08em', textTransform:'uppercase' }}>
            {tr('landing.free_join_note')}
          </p>
        </Reveal>
      </section>


      {/* Waitlist modal — gated by landing_waitlist */}
      {features?.landing_waitlist !== false && waitlistMode && (
        <div onClick={() => !waitlistSent && setWaitlistMode(false)}
          style={{ position:'fixed', inset:0, background:'var(--overlay-modal)', zIndex:500, display:'flex', alignItems:'center', justifyContent:'center', padding:'1.5rem' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background:'#161616', border:'1px solid var(--gold-border)', borderRadius:16, padding:'2rem 1.5rem', maxWidth:400, width:'100%' }}>
            {waitlistSent ? (
              <div style={{ textAlign:'center' }}>
                <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'2.5rem', color:'var(--gold)', marginBottom:'0.8rem', lineHeight:1 }}>✦</div>
                <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.6rem', fontWeight:400, color:'#F2E8D0', marginBottom:'0.5rem' }}>
                  {lang === 'fr' ? 'Tu es sur la liste' : "You're on the list"}
                </h2>
                <p style={{ fontSize:'0.85rem', color:'#888', lineHeight:1.7 }}>
                  {lang === 'fr' ? "On t'enverra un code d'invitation dès que ta place est prête. L'attente ne sera pas longue." : "We'll send you an invitation code when it's your turn. The wait won't be long."}
                </p>
                <button onClick={() => setWaitlistMode(false)}
                  style={{ marginTop:'1.2rem', padding:'0.65rem 1.5rem', background:'transparent', border:'1px solid var(--gold-border)', borderRadius:99, color:'var(--gold)', fontSize:'0.82rem', cursor:'pointer', fontFamily:'Outfit' }}>
                  {lang === 'fr' ? 'Fermer' : 'Close'}
                </button>
              </div>
            ) : (
              <div>
                <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.5rem', fontWeight:400, color:'#F2E8D0', marginBottom:'0.3rem' }}>
                  {lang === 'fr' ? 'Demande ton invitation' : 'Request your invitation'}
                </h2>
                <p style={{ fontSize:'0.82rem', color:'#888', marginBottom:'1.2rem', lineHeight:1.6 }}>
                  {lang === 'fr' ? "BeeLove grandit par invitation. Laisse ton email et on t'envoie un code." : "BeeLove is growing by invitation. Leave your email and we'll send you a code."}
                </p>
                <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem', marginBottom:'1rem' }}>
                  <input value={waitlistName} onChange={e => setWaitlistName(e.target.value)}
                    placeholder={lang === 'fr' ? 'Ton prénom' : 'Your first name'}
                    style={{ padding:'0.7rem 0.9rem', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, color:'#F2E8D0', fontFamily:'Outfit', fontSize:'0.85rem', outline:'none' }}
                  />
                  <input type="email" value={waitlistEmail} onChange={e => setWaitlistEmail(e.target.value)}
                    placeholder="your@email.com"
                    onKeyDown={e => e.key === 'Enter' && submitWaitlist()}
                    style={{ padding:'0.7rem 0.9rem', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(201,168,76,0.4)', borderRadius:8, color:'#F2E8D0', fontFamily:'Outfit', fontSize:'0.85rem', outline:'none' }}
                  />
                </div>
                <button onClick={submitWaitlist} disabled={!waitlistEmail.trim()}
                  style={{ width:'100%', padding:'0.8rem', background:'linear-gradient(135deg,#D4A84C,#B8922A)', border:'none', borderRadius:8, color:'#0A0A0A', fontFamily:'Outfit', fontWeight:700, fontSize:'0.9rem', cursor:!waitlistEmail.trim()?'not-allowed':'pointer', opacity:!waitlistEmail.trim()?0.5:1 }}>
                  {lang === 'fr' ? "Rejoindre la liste d'attente →" : 'Join the waitlist →'}
                </button>
                <p style={{ textAlign:'center', fontSize:'0.72rem', color:'#666', marginTop:'0.7rem' }}>
                  {lang === 'fr' ? 'Tu as déjà un code ?' : 'Already have a code?'}{' '}
                  <button onClick={() => { setWaitlistMode(false); onGetStarted(); }}
                    style={{ background:'none', border:'none', color:'#C9A84C', cursor:'pointer', fontSize:'0.72rem', textDecoration:'underline', padding:0 }}>
                    {lang === 'fr' ? 'Connecte-toi' : 'Sign in or use it here'}
                  </button>
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── FOOTER ── */}
      <footer style={{ borderTop:`1px solid ${G.border}`, padding:'clamp(2.5rem,6vw,4rem) clamp(1.25rem,6vw,3rem) clamp(1.5rem,4vw,2.5rem)' }}>
        <div style={{ maxWidth:1300, margin:'0 auto' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:'2.5rem', marginBottom:'2rem' }}>
            <style>{`@media(min-width:768px){.footer-grid{grid-template-columns:2fr 1fr 1fr 1fr!important}}`}</style>
            <div className="footer-grid" style={{ display:'grid', gridTemplateColumns:'1fr', gap:'2.5rem' }}>
              <div>
                <div style={{ marginBottom:'.8rem' }}>
                  <BeeloveLogo variant="compact" height={28} animate={false} themeOverride="dark" />
                </div>
                <p style={{ fontSize:'.8rem', color:G.muted, lineHeight:1.7, maxWidth:280 }}>
                  {tr('landing.cta_sub')}
                </p>
              </div>
              {[
                { title:tr('landing.footer_product'), links:[tr('landing.footer_how'),tr('landing.footer_pricing'),tr('landing.footer_signup'),tr('landing.footer_signin')] },
                { title:tr('landing.footer_links_company'), links:[tr('landing.footer_about'),tr('landing.footer_blog'),tr('landing.footer_press'),tr('landing.footer_careers')] },
                { title:tr('landing.footer_links_legal'),   links:[tr('landing.footer_privacy'),tr('landing.footer_terms'),tr('landing.footer_cookies'),tr('landing.footer_contact')] },
              ].map(col => (
                <div key={col.title}>
                  <h5 style={{ fontSize:'.62rem', letterSpacing:'.18em', textTransform:'uppercase', color:G.muted, marginBottom:'1rem' }}>{col.title}</h5>
                  {col.links.map(l => (
                    <button key={l} className="lp-footer-link" onClick={()=>{ const dest = ({
                      [tr('landing.footer_how')]:'how',[tr('landing.footer_pricing')]:'pricing',[tr('landing.footer_signup')]:'auth',[tr('landing.footer_signin')]:'auth',
                      [tr('landing.footer_about')]:'about',[tr('landing.footer_blog')]:'blog',[tr('landing.footer_press')]:'press',[tr('landing.footer_careers')]:'careers',
                      [tr('landing.footer_privacy')]:'privacy',[tr('landing.footer_terms')]:'terms',[tr('landing.footer_cookies')]:'cookies',[tr('landing.footer_contact')]:'contact',
                    })[l] || l.toLowerCase().replace(/\s+/g,'-'); import('../utils/navigate').then(({ navigate }) => navigate(dest)); }} style={{ display:'block', background:'none', border:'none', fontSize:'.82rem', color:G.muted, marginBottom:'.5rem', cursor:'pointer', fontFamily:sans, padding:0, transition:'color .2s', textAlign:'left' }}>{l}</button>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div style={{ paddingTop:'1.5rem', borderTop:`1px solid ${G.border}`, display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'1rem' }}>
            <p style={{ fontSize:'.7rem', color:G.muted2 }}>{tr('landing.all_rights')}</p>
            <div style={{ display:'flex', alignItems:'center', gap:'1rem' }}>
              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                style={{ display:'flex', alignItems:'center', gap:'.4rem', background:'none', border:`1px solid ${G.border}`, borderRadius:99, padding:'.25rem .7rem', cursor:'pointer', fontFamily:sans, fontSize:'.7rem', color:G.muted, transition:'all .2s' }}>
                {theme === 'dark'
                  ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
                  : <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
                }
                {theme === 'dark' ? (tr('nav.theme_light') || 'Light') : (tr('nav.theme_dark') || 'Dark')}
              </button>
              <span style={{ fontFamily:serif, fontStyle:'italic', fontSize:'.85rem', color:G.gold }}>Bee The One ✦</span>
            </div>
          </div>
        </div>
      </footer>

      {/* RGPD / Loi 25 cookie consent */}
      <CookieBanner lang={lang}/>

    </div>
  );
}
